import { useState, useEffect } from 'react';

import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Plus, Edit, Trash2, Tag, ArrowRightLeft, RefreshCw, Clock, Settings2, Bell, Lock, Loader2, Building, Users, UserPlus, Shield, Key } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Settings = () => {
  const { token, isAdmin } = useAuth();
  const [statusCatalog, setStatusCatalog] = useState([]);
  const [statusMappings, setStatusMappings] = useState([]);
  const [alertSettings, setAlertSettings] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showMappingDialog, setShowMappingDialog] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [editingMapping, setEditingMapping] = useState(null);

  // Organization - ÉPICA 3.4
  const [organization, setOrganization] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);
  const [editingOrg, setEditingOrg] = useState(false);
  const [savingOrg, setSavingOrg] = useState(false);
  const [orgForm, setOrgForm] = useState({ nombre: '', dominio: '' });

  // User invite - ÉPICA 3.4
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', nombre: '', role: 'OPERADOR' });
  const [inviteResult, setInviteResult] = useState(null);

  // Security - Change Password - ÉPICA 3.3
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  // Admin Password Reset - ÉPICA 3.4
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  const [newResetPassword, setNewResetPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);

  const [statusForm, setStatusForm] = useState({
    status_name: '',
    color: '#3B82F6',
    description: '',
    order: 0
  });

  const [mappingForm, setMappingForm] = useState({
    estatus_impi_pattern: '',
    status_interno_id: '',
    priority: 0
  });

  // Sync Job Monitoring - P0
  const [activeJob, setActiveJob] = useState(null);
  const [isLooping, setIsLooping] = useState(false);
  const [loopStats, setLoopStats] = useState({ batches: 0, totalProcessed: 0 });

  useEffect(() => {
    // Initial check
    fetchActiveJob();
  }, []);

  useEffect(() => {
    let interval;
    if (activeJob && (activeJob.status === 'running' || activeJob.status === 'pending')) {
      interval = setInterval(fetchActiveJob, 3000);
    }
    return () => clearInterval(interval);
  }, [activeJob]);

  async function fetchActiveJob() {
    try {
      const res = await axios.get(`${API}/sync/current-job`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data && res.data.status !== 'idle') {
        setActiveJob(res.data);
      } else if (activeJob) {
        // Job finalized transition
        const finishedJob = activeJob;
        setActiveJob(null);
        await fetchData(); // Refresh stats immediately

        // Batch Loop Logic
        if (isLooping) {
          handleBatchLoopContinue(finishedJob);
        } else {
          toast.success('Sincronización masiva completada');
        }
      }
    } catch (e) {
      console.error('Error poll sync job:', e);
    }
  };

  const handleBatchLoopContinue = async (finishedJob) => {
    // Logic: If the last job processed items, assume there might be more.
    // If processed 0, we are done.
    // Better heuristic: Check pending count from sync status

    const processedCount = finishedJob.progress?.current || 0;
    setLoopStats(prev => ({
      batches: prev.batches + 1,
      totalProcessed: prev.totalProcessed + processedCount
    }));

    // If we processed items, likely more remain (or we just finished exact chunk)
    // Let's check pending sync count safely
    try {
      const statusRes = await axios.get(`${API}/sync/status`, { headers: { Authorization: `Bearer ${token}` } });
      const pending = statusRes.data?.pending_sync || 0;

      if (pending > 0 && processedCount > 0) {
        // Trigger next batch after short delay
        toast.info(`Lote completado (${processedCount} marcas). Iniciando siguiente lote...`, { duration: 2000 });
        setTimeout(initiateNextBatch, 1500);
      } else {
        // Done
        setIsLooping(false);
        toast.success(`Sincronización Completa. Total procesado: ${loopStats.totalProcessed + processedCount} marcas.`);
      }
    } catch (e) {
      setIsLooping(false);
    }
  };

  async function initiateNextBatch() {
    try {
      const res = await axios.post(`${API}/sync/trademarks`, { sync_all: true }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        // Job started, poller will pick it up
        fetchActiveJob();
      } else {
        setIsLooping(false);
      }
    } catch (e) {
      setIsLooping(false);
      toast.error("Error al iniciar siguiente lote");
    }
  }

  async function handleManualSync() {
    if (isLooping) return; // Prevent double click

    // Reset Stats
    setLoopStats({ batches: 0, totalProcessed: 0 });
    setIsLooping(true);

    try {
      toast.info('Iniciando sincronización masiva por lotes...');
      initiateNextBatch();
    } catch (error) {
      console.error('Error starting sync:', error);
      toast.error('Error al iniciar sincronización');
      setIsLooping(false);
    }
  };

  const handleStopLoop = () => {
    setIsLooping(false);
    toast.info("Sincronización se detendrá al finalizar el lote actual.");
  };

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      const [catalogRes, mappingsRes, settingsRes, syncRes] = await Promise.all([
        axios.get(`${API}/status-catalog`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/status-mappings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/alert-settings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/sync/status`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setStatusCatalog(catalogRes.data);
      setStatusMappings(mappingsRes.data);
      setAlertSettings(settingsRes.data);
      setSyncStatus(syncRes.data);

      // Cargar organización - ÉPICA 3.4
      try {
        const orgRes = await axios.get(`${API}/organization`, { headers: { Authorization: `Bearer ${token}` } });
        setOrganization(orgRes.data);
        setOrgForm({ nombre: orgRes.data.nombre || '', dominio: orgRes.data.dominio || '' });

        // Cargar usuarios si es admin
        const usersRes = await axios.get(`${API}/organization/users`, { headers: { Authorization: `Bearer ${token}` } });
        setOrgUsers(usersRes.data);
      } catch (e) {
        // No es admin o error
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/alert-settings`, alertSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Configuración guardada');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateStatus = async () => {
    try {
      await axios.post(`${API}/status-catalog`, statusForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Estatus creado exitosamente');
      setShowStatusDialog(false);
      resetStatusForm();
      fetchData();
    } catch (error) {
      toast.error('Error al crear estatus');
    }
  };

  const handleUpdateStatus = async () => {
    try {
      await axios.put(`${API}/status-catalog/${editingStatus.id}`, statusForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Estatus actualizado');
      setShowStatusDialog(false);
      setEditingStatus(null);
      resetStatusForm();
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar estatus');
    }
  };

  const handleCreateMapping = async () => {
    try {
      await axios.post(`${API}/status-mappings`, mappingForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mapping creado');
      setShowMappingDialog(false);
      resetMappingForm();
      fetchData();
    } catch (error) {
      toast.error('Error al crear mapping');
    }
  };

  const handleUpdateMapping = async () => {
    try {
      await axios.put(`${API}/status-mappings/${editingMapping.id}`, mappingForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mapping actualizado');
      setShowMappingDialog(false);
      setEditingMapping(null);
      resetMappingForm();
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar mapping');
    }
  };

  const handleDeleteMapping = async (id) => {
    if (!window.confirm('¿Eliminar este mapping?')) return;
    try {
      await axios.delete(`${API}/status-mappings/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mapping eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar mapping');
    }
  };

  const resetStatusForm = () => {
    setStatusForm({ status_name: '', color: '#3B82F6', description: '', order: 0 });
  };

  const resetMappingForm = () => {
    setMappingForm({ estatus_impi_pattern: '', status_interno_id: '', priority: 0 });
  };

  const openEditStatus = (status) => {
    setEditingStatus(status);
    setStatusForm({
      status_name: status.status_name,
      color: status.color,
      description: status.description || '',
      order: status.order
    });
    setShowStatusDialog(true);
  };

  const openEditMapping = (mapping) => {
    setEditingMapping(mapping);
    setMappingForm({
      estatus_impi_pattern: mapping.estatus_impi_pattern,
      status_interno_id: mapping.status_interno_id,
      priority: mapping.priority
    });
    setShowMappingDialog(true);
  };

  // ÉPICA 3.3: Change Password
  const handleChangePassword = async () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword) {
      toast.error('Completa todos los campos');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(`${API}/security/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Contraseña actualizada exitosamente');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    } finally {
      setChangingPassword(false);
    }
  };

  // ÉPICA 3.4: Organización del despacho
  const handleSaveOrganization = async () => {
    setSavingOrg(true);
    try {
      await axios.put(`${API}/organization`, orgForm, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Organización actualizada');
      setOrganization(prev => ({ ...prev, ...orgForm }));
      setEditingOrg(false);
    } catch (error) {
      console.error('Error saving organization:', error);
      toast.error('Error al guardar organización');
    } finally {
      setSavingOrg(false);
    }
  };

  const handleInviteUser = async () => {
    if (!inviteForm.email || !inviteForm.nombre) {
      toast.error('Email y nombre son requeridos');
      return;
    }

    setInviting(true);
    try {
      const res = await axios.post(`${API}/organization/users/invite`, inviteForm, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Usuario invitado exitosamente');
      setInviteResult(res.data);
      fetchData(); // Recargar usuarios
    } catch (error) {
      console.error('Error inviting user:', error);
      toast.error(error.response?.data?.detail || 'Error al invitar usuario');
    } finally {
      setInviting(false);
    }
  };

  const handleChangeUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/organization/users/${userId}/role`, { role: newRole }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Rol actualizado');
      fetchData();
    } catch (error) {
      console.error('Error changing role:', error);
      toast.error(error.response?.data?.detail || 'Error al cambiar rol');
    }
  };

  const handleDeactivateUser = async (userId, userName) => {
    if (!window.confirm(`¿Desactivar al usuario ${userName}?`)) return;

    try {
      await axios.delete(`${API}/organization/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Usuario desactivado');
      fetchData();
    } catch (error) {
      console.error('Error deactivating user:', error);
      toast.error(error.response?.data?.detail || 'Error al desactivar usuario');
    }
  };

  const openResetPasswordModal = (user) => {
    setUserToReset(user);
    setNewResetPassword('');
    setResetModalOpen(true);
  };

  const handleResetPassword = async () => {
    if (!newResetPassword || newResetPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setResettingPassword(true);
    try {
      await axios.put(`${API}/organization/users/${userToReset.id}/reset-password`, {
        new_password: newResetPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Contraseña de ${userToReset.nombre} actualizada`);
      setResetModalOpen(false);
      setUserToReset(null);
      setNewResetPassword('');
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error(error.response?.data?.detail || 'Error al resetear contraseña');
    } finally {
      setResettingPassword(false);
    }
  };

  const getStatusName = (statusId) => {
    const status = statusCatalog.find(s => s.id === statusId);
    return status ? status.status_name : 'Desconocido';
  };

  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Configuración</h2>
          <p className="text-sm text-gray-500 mt-1">Gestionar catálogo de estatus, mappings y sincronización</p>
        </div>

        <Tabs defaultValue="alerts" className="space-y-6">
          <TabsList className="bg-gray-100 p-1 flex-wrap">
            <TabsTrigger value="alerts" className="data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-gray-600">
              <Bell className="h-4 w-4 mr-2" />
              Alertas
            </TabsTrigger>
            <TabsTrigger value="security" className="data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-gray-600">
              <Lock className="h-4 w-4 mr-2" />
              Seguridad
            </TabsTrigger>
            {/* ÉPICA 3.4: Organización */}
            {isAdmin() && (
              <TabsTrigger value="organization" className="data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-gray-600">
                <Building className="h-4 w-4 mr-2" />
                Organización
              </TabsTrigger>
            )}
            <TabsTrigger value="sync" className="data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-gray-600">
              <RefreshCw className="h-4 w-4 mr-2" />
              Sincronización
            </TabsTrigger>
            <TabsTrigger value="catalog" className="data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-gray-600">
              <Tag className="h-4 w-4 mr-2" />
              Catálogo
            </TabsTrigger>
            <TabsTrigger value="mappings" className="data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-gray-600">
              <ArrowRightLeft className="h-4 w-4 mr-2" />
              Mappings
            </TabsTrigger>
          </TabsList>

          {/* Alert Rules Settings - ÉPICA 3.2 */}
          <TabsContent value="alerts">
            <div className="space-y-6">
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Bell className="h-4 w-4" />
                  Reglas de Generación de Alertas
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Configura cuándo se generan las alertas y cuándo se archivan automáticamente.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <Label>Ventana de Renovación (días)</Label>
                    <Input
                      type="number"
                      value={alertSettings?.renewal_window_days || 60}
                      onChange={(e) => setAlertSettings({ ...alertSettings, renewal_window_days: parseInt(e.target.value) })}
                      className="mt-1"
                      min={1}
                      max={365}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Días antes del vencimiento para generar alertas de renovación
                    </p>
                  </div>
                  <div>
                    <Label>Ventana de Declaración de Uso (días)</Label>
                    <Input
                      type="number"
                      value={alertSettings?.declaration_window_days || 60}
                      onChange={(e) => setAlertSettings({ ...alertSettings, declaration_window_days: parseInt(e.target.value) })}
                      className="mt-1"
                      min={1}
                      max={365}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Días antes para alertas de declaración de uso
                    </p>
                  </div>
                  <div>
                    <Label>Período de Gracia (días)</Label>
                    <Input
                      type="number"
                      value={alertSettings?.overdue_grace_days || 365}
                      onChange={(e) => setAlertSettings({ ...alertSettings, overdue_grace_days: parseInt(e.target.value) })}
                      className="mt-1"
                      min={30}
                      max={730}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Días después del vencimiento antes de archivar alertas
                    </p>
                  </div>
                </div>

                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mt-6">
                  <p className="text-sm text-indigo-800">
                    <strong>Nota:</strong> Las alertas con más de {alertSettings?.overdue_grace_days || 365} días de vencimiento
                    serán archivadas automáticamente y no recibirán recordatorios.
                  </p>
                </div>

                <div className="flex justify-end pt-6 border-t border-gray-100 mt-6">
                  <Button onClick={handleSaveSettings} disabled={saving}>
                    {saving ? 'Guardando...' : 'Guardar configuración'}
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Security Settings - ÉPICA 3.3 */}
          <TabsContent value="security">
            <div className="space-y-6">
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  Cambiar Contraseña
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Actualiza tu contraseña de acceso. Se requiere la contraseña actual para confirmar.
                </p>

                <div className="max-w-md space-y-4">
                  <div>
                    <Label>Contraseña actual</Label>
                    <Input
                      type="password"
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                      className="mt-1"
                      placeholder="••••••••"
                      data-testid="current-password-input"
                    />
                  </div>
                  <div>
                    <Label>Nueva contraseña</Label>
                    <Input
                      type="password"
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                      className="mt-1"
                      placeholder="••••••••"
                      data-testid="new-password-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Mínimo 6 caracteres</p>
                  </div>
                  <div>
                    <Label>Confirmar nueva contraseña</Label>
                    <Input
                      type="password"
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                      className="mt-1"
                      placeholder="••••••••"
                      data-testid="confirm-password-input"
                    />
                  </div>

                  <Button
                    onClick={handleChangePassword}
                    disabled={changingPassword || !passwordForm.currentPassword || !passwordForm.newPassword}
                    className="mt-4"
                    data-testid="change-password-btn"
                  >
                    {changingPassword ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Actualizando...
                      </>
                    ) : (
                      'Actualizar contraseña'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ÉPICA 3.4: Organization Settings */}
          {isAdmin() && (
            <TabsContent value="organization">
              <div className="space-y-6">
                {/* Información del Despacho */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Building className="h-4 w-4" />
                    Información del Despacho
                  </h3>

                  {editingOrg ? (
                    <div className="max-w-md space-y-4">
                      <div>
                        <Label>Nombre del Despacho</Label>
                        <Input
                          value={orgForm.nombre}
                          onChange={(e) => setOrgForm({ ...orgForm, nombre: e.target.value })}
                          className="mt-1"
                          data-testid="org-name-input"
                        />
                      </div>
                      <div>
                        <Label>Dominio</Label>
                        <Input
                          value={orgForm.dominio}
                          onChange={(e) => setOrgForm({ ...orgForm, dominio: e.target.value })}
                          className="mt-1"
                          placeholder="ej: despacho.com"
                          data-testid="org-domain-input"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={handleSaveOrganization} disabled={savingOrg}>
                          {savingOrg ? 'Guardando...' : 'Guardar'}
                        </Button>
                        <Button variant="outline" onClick={() => setEditingOrg(false)}>
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500">Nombre</p>
                          <p className="font-medium text-gray-900">{organization?.nombre || 'Sin nombre'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Dominio</p>
                          <p className="font-medium text-gray-900">{organization?.dominio || 'Sin dominio'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Usuarios activos</p>
                          <p className="font-medium text-gray-900">{organization?.user_count || 0}</p>
                        </div>
                      </div>
                      <Button variant="outline" onClick={() => setEditingOrg(true)}>
                        <Edit className="mr-2 h-4 w-4" />
                        Editar información
                      </Button>
                    </div>
                  )}
                </div>

                {/* Usuarios del Despacho */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Usuarios del Despacho
                    </h3>
                    <Button
                      onClick={() => { setInviteModalOpen(true); setInviteForm({ email: '', nombre: '', role: 'OPERADOR' }); setInviteResult(null); }}
                      data-testid="invite-user-btn"
                    >
                      <UserPlus className="mr-2 h-4 w-4" />
                      Invitar usuario
                    </Button>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rol</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {orgUsers.map(user => (
                          <tr key={user.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">{user.nombre}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{user.email}</td>
                            <td className="px-4 py-3">
                              <Select
                                value={user.role}
                                onValueChange={(value) => handleChangeUserRole(user.id, value)}
                              >
                                <SelectTrigger className="w-[150px] h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="ADMIN_DESPACHO">Administrador</SelectItem>
                                  <SelectItem value="OPERADOR">Operador</SelectItem>
                                  <SelectItem value="CLIENTE">Cliente</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.activo ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                {user.activo ? 'Activo' : 'Inactivo'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              {user.activo && (
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openResetPasswordModal(user)}
                                    className="text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                                    title="Resetear contraseña"
                                  >
                                    <Key className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeactivateUser(user.id, user.nombre)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    title="Desactivar usuario"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </TabsContent>
          )}

          {/* Sync Settings */}
          <TabsContent value="sync">
            <div className="space-y-6">
              {/* Sync Status Card */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Estado de Sincronización
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-2xl font-semibold text-gray-900">{syncStatus?.total_trademarks || 0}</p>
                    <p className="text-sm text-gray-500">Total marcas</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <p className="text-2xl font-semibold text-green-600">{syncStatus?.recently_synced || 0}</p>
                    <p className="text-sm text-gray-500">Sync últimas 24h</p>
                  </div>
                  <div className="bg-indigo-50 rounded-lg p-4">
                    <p className="text-2xl font-semibold text-indigo-600">{syncStatus?.never_synced || 0}</p>
                    <p className="text-sm text-gray-500">Nunca sincronizadas</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-2xl font-semibold text-blue-600">{syncStatus?.pending_sync || 0}</p>
                    <p className="text-sm text-gray-500">Pendientes de sync</p>
                  </div>
                </div>
              </div>

              {/* Sync Configuration */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Settings2 className="h-4 w-4" />
                  Configuración de Sincronización
                </h3>

                {/* Active Job Banner */}
                {activeJob && (activeJob.status === 'running' || activeJob.status === 'pending') && (
                  <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4 animate-pulse">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <RefreshCw className="h-4 w-4 text-blue-600 animate-spin" />
                        <span className="font-semibold text-blue-800">Sincronización en curso...</span>
                      </div>
                      <span className="text-xs text-blue-600 font-mono">
                        {activeJob.progress?.percent || 0}%
                      </span>
                    </div>

                    <div className="w-full bg-blue-200 rounded-full h-2 mb-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${activeJob.progress?.percent || 0}%` }}
                      ></div>
                    </div>

                    <div className="flex justify-between text-xs text-blue-700">
                      <span>
                        Marca {activeJob.progress?.current || 0} de {activeJob.progress?.total || '?'}
                      </span>
                      <span className="truncate max-w-[200px]">
                        {activeJob.progress?.current_trademark_name || activeJob.progress?.current_trademark || 'Iniciando...'}
                      </span>
                    </div>
                  </div>
                )}

                {/* Last Sync Alert - P1 FEATURE */}
                {syncStatus?.last_completed_job && (
                  <div className={`mb-6 border rounded-lg p-4 flex items-center gap-3 ${syncStatus.last_completed_job.status === 'completed'
                    ? 'bg-green-50 border-green-200 text-green-800'
                    : 'bg-yellow-50 border-yellow-200 text-yellow-800'
                    }`}>
                    <Clock className="h-4 w-4" />
                    <div>
                      <p className="text-sm font-semibold">
                        Última sincronización masiva: {new Date(syncStatus.last_completed_job.created_at).toLocaleString()}
                      </p>
                      {syncStatus.last_completed_job.error && (
                        <p className="text-xs mt-1">
                          Error: {syncStatus.last_completed_job.error}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div className="space-y-6">
                  {/* Auto Sync Toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-medium">Sincronización automática diaria</Label>
                      <p className="text-xs text-gray-500 mt-1">Sincroniza todas las marcas automáticamente cada día</p>
                    </div>
                    <Switch
                      checked={alertSettings?.enable_auto_sync || false}
                      onCheckedChange={(checked) => setAlertSettings({ ...alertSettings, enable_auto_sync: checked })}
                    />
                  </div>

                  {alertSettings?.enable_auto_sync && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-100">
                      <div>
                        <Label>Hora de ejecución</Label>
                        <Select
                          value={String(alertSettings?.auto_sync_hour || 3)}
                          onValueChange={(value) => setAlertSettings({ ...alertSettings, auto_sync_hour: parseInt(value) })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 24 }, (_, i) => (
                              <SelectItem key={i} value={String(i)}>{i.toString().padStart(2, '0')}:00</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Límite por batch</Label>
                        <Input
                          type="number"
                          value={alertSettings?.sync_batch_size || 50}
                          onChange={(e) => setAlertSettings({ ...alertSettings, sync_batch_size: parseInt(e.target.value) })}
                          className="mt-1"
                          min={1}
                          max={500}
                        />
                        <p className="text-xs text-gray-500 mt-1">Máx. marcas por ejecución</p>
                      </div>
                      <div>
                        <Label>Delay entre requests (seg)</Label>
                        <Input
                          type="number"
                          value={alertSettings?.sync_delay_seconds || 5}
                          onChange={(e) => setAlertSettings({ ...alertSettings, sync_delay_seconds: parseInt(e.target.value) })}
                          className="mt-1"
                          min={1}
                          max={60}
                        />
                        <p className="text-xs text-gray-500 mt-1">Para no saturar IMPI</p>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                    <div className="flex gap-2">
                      {isLooping && (
                        <Button variant="outline" onClick={handleStopLoop} className="text-red-600 border-red-200 hover:bg-red-50">
                          <Loader2 className="mr-2 h-4 w-4 animate-spin text-red-600" />
                          Detener
                        </Button>
                      )}
                      <Button
                        variant="secondary"
                        onClick={handleManualSync}
                        disabled={(activeJob && (activeJob.status === 'running' || activeJob.status === 'pending')) || isLooping}
                      >
                        <RefreshCw className={`mr-2 h-4 w-4 ${isLooping ? 'animate-spin' : ''}`} />
                        {isLooping ? 'Procesando lotes...' : 'Iniciar sincronización ahora'}
                      </Button>
                    </div>

                    <Button onClick={handleSaveSettings} disabled={saving}>
                      {saving ? 'Guardando...' : 'Guardar configuración'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Recent Sync Events */}
              {syncStatus?.last_sync_events?.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">Últimos eventos de sincronización</h3>
                  <div className="space-y-2">
                    {syncStatus.last_sync_events.slice(0, 5).map((event, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${event.event_type === 'SYNC_UPDATED' ? 'bg-green-100 text-green-700' :
                            event.event_type === 'SYNC_NO_CHANGES' ? 'bg-gray-100 text-gray-700' :
                              event.event_type === 'SYNC_ERROR' ? 'bg-red-100 text-red-700' :
                                'bg-blue-100 text-blue-700'
                            }`}>
                            {event.event_type.replace('SYNC_', '')}
                          </span>
                          <span className="text-sm text-gray-600">{event.description}</span>
                        </div>
                        <span className="text-xs text-gray-400">
                          {new Date(event.created_at).toLocaleString('es-MX')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Status Catalog */}
          <TabsContent value="catalog">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-500">Define los estatus internos del despacho</p>
                <Button
                  onClick={() => { resetStatusForm(); setShowStatusDialog(true); }}
                  className="bg-gray-900 hover:bg-gray-800"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Nuevo Estatus
                </Button>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vista Previa</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Color</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Orden</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {statusCatalog.map(status => (
                      <tr key={status.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-900">{status.status_name}</td>
                        <td className="px-6 py-4">
                          <span
                            className="status-chip"
                            style={{
                              backgroundColor: `${status.color}15`,
                              color: status.color,
                              borderColor: `${status.color}40`
                            }}
                          >
                            {status.status_name}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <div
                              className="h-4 w-4 rounded border border-gray-300"
                              style={{ backgroundColor: status.color }}
                            ></div>
                            <span className="text-xs font-mono text-gray-600">{status.color}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{status.order}</td>
                        <td className="px-6 py-4 text-right">
                          <Button variant="ghost" size="icon" onClick={() => openEditStatus(status)} className="h-8 w-8">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Mappings */}
          <TabsContent value="mappings">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-500">Mapeo de estatus IMPI a estatus internos</p>
                <Button
                  onClick={() => { resetMappingForm(); setShowMappingDialog(true); }}
                  className="bg-gray-900 hover:bg-gray-800"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Nuevo Mapping
                </Button>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estatus IMPI</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estatus Interno</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prioridad</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {statusMappings.map(mapping => (
                      <tr key={mapping.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <span className="text-sm font-mono text-gray-900">{mapping.estatus_impi_pattern}</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">{getStatusName(mapping.status_interno_id)}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{mapping.priority}</td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="icon" onClick={() => openEditMapping(mapping)} className="h-8 w-8">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteMapping(mapping.id)} className="h-8 w-8 text-red-600">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Status Dialog */}
        <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingStatus ? 'Editar Estatus' : 'Nuevo Estatus'}</DialogTitle>
              <DialogDescription>Define el nombre, color y orden del estatus</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nombre del Estatus</Label>
                <Input
                  value={statusForm.status_name}
                  onChange={(e) => setStatusForm({ ...statusForm, status_name: e.target.value })}
                  placeholder="Ej: Registrada"
                />
              </div>
              <div>
                <Label>Color (Hex)</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={statusForm.color}
                    onChange={(e) => setStatusForm({ ...statusForm, color: e.target.value })}
                    className="w-16"
                  />
                  <Input
                    value={statusForm.color}
                    onChange={(e) => setStatusForm({ ...statusForm, color: e.target.value })}
                    placeholder="#3B82F6"
                  />
                </div>
              </div>
              <div>
                <Label>Descripción (opcional)</Label>
                <Input
                  value={statusForm.description}
                  onChange={(e) => setStatusForm({ ...statusForm, description: e.target.value })}
                />
              </div>
              <div>
                <Label>Orden</Label>
                <Input
                  type="number"
                  value={statusForm.order}
                  onChange={(e) => setStatusForm({ ...statusForm, order: parseInt(e.target.value) })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowStatusDialog(false)}>Cancelar</Button>
              <Button onClick={editingStatus ? handleUpdateStatus : handleCreateStatus}>
                {editingStatus ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Mapping Dialog */}
        <Dialog open={showMappingDialog} onOpenChange={setShowMappingDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingMapping ? 'Editar Mapping' : 'Nuevo Mapping'}</DialogTitle>
              <DialogDescription>Mapeo de estatus IMPI a estatus internos</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Estatus IMPI (texto o pattern)</Label>
                <Input
                  value={mappingForm.estatus_impi_pattern}
                  onChange={(e) => setMappingForm({ ...mappingForm, estatus_impi_pattern: e.target.value })}
                  placeholder="REGISTRADA"
                />
              </div>
              <div>
                <Label>Estatus Interno</Label>
                <select
                  value={mappingForm.status_interno_id}
                  onChange={(e) => setMappingForm({ ...mappingForm, status_interno_id: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-gray-300"
                >
                  <option value="">Seleccionar estatus</option>
                  {statusCatalog.map(status => (
                    <option key={status.id} value={status.id}>{status.status_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Prioridad</Label>
                <Input
                  type="number"
                  value={mappingForm.priority}
                  onChange={(e) => setMappingForm({ ...mappingForm, priority: parseInt(e.target.value) })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowMappingDialog(false)}>Cancelar</Button>
              <Button onClick={editingMapping ? handleUpdateMapping : handleCreateMapping}>
                {editingMapping ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ÉPICA 3.4: Modal de Invitación de Usuario */}
        <Dialog open={inviteModalOpen} onOpenChange={(open) => { if (!open) { setInviteResult(null); } setInviteModalOpen(open); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5" />
                Invitar Usuario
              </DialogTitle>
              <DialogDescription>
                Agrega un nuevo usuario a tu despacho. Se generará una contraseña temporal.
              </DialogDescription>
            </DialogHeader>

            {inviteResult ? (
              <div className="space-y-4 py-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-800 font-medium mb-2">Usuario creado exitosamente</p>
                  <div className="space-y-2 text-sm">
                    <p><strong>Email:</strong> {inviteResult.user?.email}</p>
                    <p><strong>Contraseña temporal:</strong> <code className="bg-green-100 px-2 py-1 rounded">{inviteResult.temp_password}</code></p>
                  </div>
                  <p className="text-xs text-green-700 mt-3">
                    Comparta esta contraseña de forma segura. El usuario deberá cambiarla en su primer inicio de sesión.
                  </p>
                </div>
                <Button className="w-full" onClick={() => { setInviteModalOpen(false); setInviteResult(null); }}>
                  Cerrar
                </Button>
              </div>
            ) : (
              <>
                <div className="space-y-4 py-4">
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={inviteForm.email}
                      onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                      placeholder="usuario@email.com"
                      className="mt-1"
                      data-testid="invite-email-input"
                    />
                  </div>
                  <div>
                    <Label>Nombre completo *</Label>
                    <Input
                      value={inviteForm.nombre}
                      onChange={(e) => setInviteForm({ ...inviteForm, nombre: e.target.value })}
                      placeholder="Juan Pérez"
                      className="mt-1"
                      data-testid="invite-name-input"
                    />
                  </div>
                  <div>
                    <Label>Rol</Label>
                    <Select
                      value={inviteForm.role}
                      onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ADMIN_DESPACHO">
                          <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4 text-red-500" />
                            Administrador
                          </div>
                        </SelectItem>
                        <SelectItem value="OPERADOR">
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-blue-500" />
                            Operador
                          </div>
                        </SelectItem>
                        <SelectItem value="CLIENTE">
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-gray-500" />
                            Cliente
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setInviteModalOpen(false)}>
                    Cancelar
                  </Button>
                  <Button onClick={handleInviteUser} disabled={inviting || !inviteForm.email || !inviteForm.nombre}>
                    {inviting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Invitando...
                      </>
                    ) : (
                      'Invitar usuario'
                    )}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
        {/* Modal de Reset Password */}
        <Dialog open={resetModalOpen} onOpenChange={setResetModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                Resetear Contraseña
              </DialogTitle>
              <DialogDescription>
                Ingresa una nueva contraseña para <strong>{userToReset?.nombre}</strong> ({userToReset?.email}).
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label>Nueva contraseña</Label>
              <Input
                type="text"
                value={newResetPassword}
                onChange={(e) => setNewResetPassword(e.target.value)}
                placeholder="Ingresa nueva contraseña"
                className="mt-1"
                autoComplete="off"
              />
              <p className="text-xs text-gray-500 mt-1">
                Asegúrate de copiar esta contraseña y enviarla al usuario de forma segura.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResetModalOpen(false)}>Cancelar</Button>
              <Button onClick={handleResetPassword} disabled={resettingPassword || !newResetPassword}>
                {resettingPassword ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Actualizar contraseña'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};
